// `GET /v1/players/:addr/detail`. The envelope is deliberately broad so MLROs load a full
// player context in one round-trip.

export const GATE_TYPES = [
    'wallet_owned',
    'photo_id',
    'proof_of_address',
    'sof_declared',
    'sof_documented',
    'sow_light',
    'sow_forensic',
    'pep_screened_clear',
    'sanctions_screened_clear',
    'adverse_media_clear',
    'enhanced_address_proof',
    'video_kyc',
    'employer_verification',
    'bank_account_verified',
    // Bit 14 (task 91): the SoF/SoW tax-residency evidence gate.
    'tax_residency_documented',
    // Bit 15 (BLO-1060): the wallet screen's sanctions verdict. Bit 8
    // (sanctions_screened_clear) is Sumsub's identity sanctions verdict.
    'wallet_sanctions_screened_clear',
] as const

export type GateType = (typeof GATE_TYPES)[number]

export const GATE_LABELS: Record<GateType, string> = {
    wallet_owned: 'Wallet ownership',
    photo_id: 'Photo ID',
    proof_of_address: 'Proof of address',
    sof_declared: 'SoF declared',
    sof_documented: 'SoF documented',
    sow_light: 'SoW (light)',
    sow_forensic: 'SoW (forensic)',
    pep_screened_clear: 'PEP clear',
    sanctions_screened_clear: 'Identity sanctions clear',
    adverse_media_clear: 'Adverse media clear',
    enhanced_address_proof: 'Enhanced address proof',
    video_kyc: 'Video KYC',
    employer_verification: 'Employer verification',
    bank_account_verified: 'Bank account verified',
    tax_residency_documented: 'Tax residency documented',
    wallet_sanctions_screened_clear: 'Wallet sanctions clear',
}

export type GateStatus = 'passed' | 'pending' | 'failed' | 'expired' | 'not_applicable'

// `players.pep_status`, `.sanctions_status` and `.adverse_media_status`, sent unchanged. Each
// column has its own CHECK set (blockpot-service migration 0002).
export type PepStatus = 'none' | 'domestic' | 'foreign' | 'intl_org' | 'rca'
export type PlayerSanctionsStatus = 'clear' | 'hit' | 'false_positive_cleared'
export type AdverseMediaStatus = 'clear' | 'hit' | 'reviewed'
export type ScreeningStatus = PepStatus | PlayerSanctionsStatus | AdverseMediaStatus

// `player_wallets.sanctions_status`, sent unchanged by `readWallets`: `compliance.WalletSanctionsStatus`
// plus `false_positive_cleared`, which the MLRO clear-sanctions action writes (migration 0024,
// BLO-943). Distinct from the player-level `ScreeningStatus`.
export type WalletSanctionsStatus =
    'clear' | 'sanctioned' | 'error' | 'mlro_review' | 'false_positive_cleared'

export type PlayerAccountStatus = 'active' | 'suspended' | 'closed' | 'blocked'

export type RiskRating = 'low' | 'medium' | 'high' | 'prohibited'

export type TierId = 'T0' | 'T1' | 'T2' | 'T3' | 'T4' | 'BLOCKED'

// `player_wallets.role` and `.status`, sent unchanged by the service: `compliance.WalletRole` and
// `compliance.WalletStatus` (BLO-940). 'deposit' / 'withdrawal' are the service's names.
export type WalletRole = 'deposit' | 'withdrawal' | 'both'

export type WalletStatus = 'clean' | 'flag' | 'edd_required' | 'blocked' | 'observed_only'

export type TierChangeTrigger = 'activity' | 'gate' | 'manual_override' | 'policy_activation'

// The `eur_ledger_entries.transaction_type` CHECK set. 'deposit' is the misnamed stake and
// 'withdrawal' a claim; the names are the service's.
export type LedgerTransactionType = 'deposit' | 'win_payout' | 'withdrawal' | 'fee_charge'

export type SequencerStatus = 'up' | 'recently_recovered' | 'down'

export const SELF_EXCLUSION_DURATIONS = ['1mo', '3mo', '6mo', '12mo', 'permanent'] as const
export type SelfExclusionDuration = (typeof SELF_EXCLUSION_DURATIONS)[number]

export type EvidenceProvider = 'sumsub' | 'internal' | 'external_upload' | 'chain_screening'

export interface PlayerSummary {
    playerId: string
    createdAt: string
    status: PlayerAccountStatus
    declaredCountry: string | null
    resolvedCountry: string | null
    isBlocked: boolean
    blockReason?: string | null
    riskRating: RiskRating
    pepStatus: PepStatus
    sanctionsStatus: PlayerSanctionsStatus
    adverseMediaStatus: AdverseMediaStatus
    lastScreenedAt?: string | null
    currentTierCached: TierId
}

// Mirrors blockpot-service `detailAttestation`. BLO-678 retired the self-declared jurisdiction;
// `resolvedCountry` is the server-resolved country, null when unresolved or on pre-v2 rows.
export interface AttestationRecord {
    attestedAt: string
    ip: string | null
    userAgent: string | null
    tosVersionHash: string
    dobSelfDeclared: string
    resolvedCountry: string | null
}

// `ownershipProvenAt` and the sanctions fields are `omitempty`: an `observed_only` wallet has no
// ownership proof, and a wallet not yet screened has no sanctions result.
export interface WalletRecord {
    address: string
    role: WalletRole
    ownershipProvenAt?: string
    totalDepositedEurMinor: number
    totalWithdrawnEurMinor: number
    headroomEurMinor: number
    sanctionsStatus?: WalletSanctionsStatus
    sanctionsCheckedAt?: string
    sanctionsSource?: string
    status: WalletStatus
}

export interface WalletOrigin {
    address: string
    originVasp: string | null
    firstSeenAt: string
}

export interface GateRecord {
    gateType: GateType
    status: GateStatus
    passedAt: string | null
    expiresAt: string | null
    evidenceRef: string | null
    evidenceProvider: EvidenceProvider | null
    // The service route that signs the evidence document; omitted when there is none.
    evidenceSignedUrl?: string
    setBy: string | null
    setAt: string | null
}

// Off-chain materialised view of cumulative activity. Authoritative for the
// fields that don't fit on chain (lastUpdatedAt). The EUR figures come from the on-chain
// operator counters; this struct is the projected fallback for the operator drift banner.
// cumEntries mirrors the on-chain inflow meter and cumClaims the outflow meter (bumped on
// every exit, direct-pays included). netEnteredEurMinor and
// largestSingleWin* are telemetry only — no gate reads them. The service
// still computes netEnteredEurMinor inline, so the field stays on the wire.
export interface CumulativeActivity {
    cumWinningsEurMinor: number
    cumEntriesEurMinor: number
    netEnteredEurMinor: number
    cumClaimsEurMinor: number
    largestSingleWinEurMinor: number
    largestSingleEntryEurMinor: number
    lastUpdatedAt: string | null
    // EIP-155 id of the chain these figures are for. The projection is per chain (BLO-990).
    chainId: number
}

// The `sof_records` / `sow_records` CHECK sets.
export type DeclaredSource =
    | 'salary'
    | 'savings'
    | 'crypto_trading'
    | 'business'
    | 'inheritance'
    | 'sale_of_asset'
    | 'gift'
    | 'other'

export type DeclarationOutcome = 'pending' | 'accepted' | 'rejected' | 'escalated'

// Mirrors blockpot-service `detailSOFRecord`. The review fields are omitempty; the service sends
// no evidence refs.
export interface SofRecord {
    sofId: string
    declaredAt: string
    declaredSource: DeclaredSource
    declaredAmountEurMinor: number
    narrative: string
    reviewedBy?: string | null
    reviewedAt?: string | null
    outcome: DeclarationOutcome
}

// Mirrors blockpot-service `detailSOWRecord`. The review and board sign-off fields are omitempty.
export interface SowRecord {
    sowId: string
    declaredAt: string
    declaredSource: DeclaredSource
    declaredAmountEurMinor: number
    narrative: string
    reviewedBy?: string | null
    reviewedAt?: string | null
    outcome: DeclarationOutcome
    boardSignoffAt?: string | null
    boardSignoffBy?: string | null
}

export interface DocumentRecord {
    documentId: string
    type: string
    sumsubDocId: string | null
    uploadedAt: string | null
    verifiedAt: string | null
    verificationOutcome: 'accepted' | 'rejected' | 'pending' | null
    retentionExpiresAt: string | null
    // The service route that signs this document, not a signed URL itself.
    signedUrl: string
}

// Mirrors blockpot-service `detailLedgerEntry`. `nativeAmount` is a wei decimal string and
// `fxEthEurDerived` a NUMERIC(38,18) string. The chain id is on the enclosing `LedgerPage`.
export interface LedgerEntry {
    entryId: string
    transactionType: LedgerTransactionType
    onChainTxHash: string
    blockNumber: number
    blockTimestamp: string
    nativeAmount: string
    nativeCurrency: string
    eurAmountMinor: number
    fxEthEurDerived: string
    sequencerStatus: SequencerStatus
}

// `nextCursor` is omitempty on the last page. `chainId` is the chain the page is for: the
// service filters the ledger to one chain (BLO-990).
export interface LedgerPage {
    entries: LedgerEntry[]
    nextCursor?: string | null
    chainId: number
}

// Mirrors blockpot-service `detailTierHistory`. `fromTier` and `note` are omitempty; `note` is the
// operator's reason on a manual override and absent on automatic entries. An override clear with
// no resulting tier sends `toTier: 'cleared'`. The service never sends `policyVersion` (BLO-935).
export interface TierHistoryEntry {
    fromTier?: TierId | null
    toTier: TierId | 'cleared'
    changedAt: string
    triggeredBy: TierChangeTrigger
    note?: string | null
}

// Mirrors blockpot-service `detailSelfExclusion`. The service omits `endsAt` for a permanent
// exclusion and `appliedByUserId` when a player applied it, and never sends `reason` on the
// detail payload.
export interface SelfExclusionRecord {
    /** Exclusion row id; the MLRO lift route addresses it. Absent from older service builds. */
    id?: string
    duration: SelfExclusionDuration
    startsAt: string
    endsAt?: string | null
    appliedBy: 'player' | 'mlro'
    appliedByUserId?: string | null
    reason?: string | null
}

// MLRO lift reasons (BLO-930, founder decision 2026-10-06). "Player asked" is
// deliberately not one: Terms §10.2(c) forbids shortening an exclusion on request.
export const SELF_EXCLUSION_LIFT_REASONS = [
    'wrong_target',
    'applied_in_error',
    'duplicate',
] as const
export type SelfExclusionLiftReason = (typeof SELF_EXCLUSION_LIFT_REASONS)[number]

// Mirrors blockpot-service `detailLossLimit`. The period is the key in `LossLimits`, so the
// service sends no `period` field (BLO-934).
export interface LossLimit {
    amountEurMinor: number
    effectiveFrom: string
}

// Mirrors blockpot-service `detailLossLimitPending` (a `limit_change_requests` row).
export interface PendingLossLimit {
    id: string
    period: 'daily' | 'weekly' | 'monthly'
    newAmountEurMinor: number
    direction: 'increase' | 'decrease'
    requestedAt: string
    effectiveAt: string
}

export interface LossLimits {
    daily: LossLimit | null
    weekly: LossLimit | null
    monthly: LossLimit | null
    pending: PendingLossLimit[]
}

// Re-exported through player-detail for backwards-compatible imports; the
// canonical shape lives in `@/types/mlro` so the MLRO surfaces and the
// player-detail surfaces never drift on case status / severity.
import type { MlroCaseSummary } from '@/types/mlro'
import type { SybilClusterStatus } from '@/types/sybil'

// Per-wallet Sybil correlation status from `wallet_correlation_status` (BLO-936), one entry per
// bound wallet. `status` is null when the wallet has no row or sits below the review threshold;
// every other field is null when it has no row. `clusterId` is the largest cluster holding the
// wallet. A clear through `sybil-clear` stamps `reviewedByAddress`, not `reviewedByUserId`.
export interface PlayerWalletSybilStatus {
    walletAddress: string
    status: SybilClusterStatus | null
    clusterId: string | null
    highestClusterSize: number | null
    reviewedAt: string | null
    reviewedByUserId: string | null
    reviewedByAddress: string | null
}

export type CaseSummary = Pick<
    MlroCaseSummary,
    'caseId' | 'title' | 'severity' | 'openedAt' | 'status'
>

export interface PlayerDetail {
    player: PlayerSummary
    attestation: AttestationRecord | null
    wallets: WalletRecord[]
    walletOrigins: WalletOrigin[]
    // Absent unless the caller is an MLRO or admin (BLO-939): the service omits the key for
    // read-only operators. Absent means "not shown to you", never "no findings".
    walletSybil?: PlayerWalletSybilStatus[]
    gates: GateRecord[]
    // No `lifetime`: the service never sends it. Lifetime EUR is read from
    // `BlockpotOperator` on chain via `useOperatorLifetime` (BLO-934).
    cumulative: CumulativeActivity
    sofRecords: SofRecord[]
    sowRecords: SowRecord[]
    documents: DocumentRecord[]
    ledger: LedgerPage
    tierHistory: TierHistoryEntry[]
    selfExclusion: SelfExclusionRecord | null
    lossLimits: LossLimits
    openCases: CaseSummary[]
    cachedAt: string
    cacheTtlSeconds: number
}

// blockpot-service `DocumentsHandler.SignedURL`: snake_case, unlike the detail payload.
// The detail payload as the service sends it. `attestation`, `selfExclusion` and each loss-limit
// period are omitempty; `normalizePlayerDetail` turns them into nulls.
export interface PlayerDetailWire extends Omit<
    PlayerDetail,
    'attestation' | 'selfExclusion' | 'lossLimits'
> {
    attestation?: AttestationRecord
    selfExclusion?: SelfExclusionRecord
    lossLimits: LossLimitsWire
}

export interface LossLimitsWire {
    daily?: LossLimit
    weekly?: LossLimit
    monthly?: LossLimit
    pending: PendingLossLimit[]
}

export interface DocumentSignedUrlResponse {
    url: string
    expires_at: string
    ttl_seconds: number
}
